// Minimal AppSync GraphQL subscription client.
//
// AppSync subscriptions use a custom WebSocket protocol — not the
// graphql-ws standard. The handshake:
//
//   1. Open WebSocket to wss://<api-id>.appsync-realtime-api.{region}.
//      amazonaws.com/graphql, with auth + an empty payload encoded
//      as base64 query string params, and subprotocol "graphql-ws".
//   2. On open: send {"type": "connection_init"}.
//   3. Wait for {"type": "connection_ack", "payload": {connectionTimeoutMs}}.
//   4. Send {"id": <uuid>, "type": "start", "payload":
//        {"data": <stringified-query>, "extensions": {"authorization": {host, Authorization}}}}.
//   5. Wait for {"id", "type": "start_ack"}. (Connection-error events are
//      delivered as type:"error".)
//   6. Receive {"id", "type": "data", "payload": {data, errors?}}.
//   7. On unmount: send {"id", "type": "stop"}; close WebSocket.
//
// We always send the Authorization header (the JWT or the "anonymous"
// sentinel) — same model as the HTTP transport. The team JWT
// authorizer allows the subscribed field if the JWT is valid.

import { useEffect, useRef, useState } from "react";

const ENDPOINT = import.meta.env.VITE_APPSYNC_URL;

function realtimeUrl(graphqlUrl: string): string {
  // https://oei47wxz2jhsvargzxyoqsvgge.appsync-api.us-east-1.amazonaws.com/graphql
  // → wss://oei47wxz2jhsvargzxyoqsvgge.appsync-realtime-api.us-east-1.amazonaws.com/graphql
  return graphqlUrl
    .replace(/^https?:\/\//i, "wss://")
    .replace("appsync-api", "appsync-realtime-api");
}

function graphqlHost(graphqlUrl: string): string {
  return new URL(graphqlUrl).host;
}

function b64(s: string): string {
  return btoa(s);
}

function currentAuthHeader(): string {
  // Same lookup as api.ts's setAuthToken — read from localStorage
  // directly. Hooks can't import api.ts's `client` defaults cleanly.
  const stored = localStorage.getItem("paymentproducts.team.jwt");
  if (stored) {
    return stored.startsWith("Bearer ") ? stored : `Bearer ${stored}`;
  }
  return "anonymous";
}

interface SubscriptionState<T> {
  /** Last received data payload. */
  data: T | null;
  /** Most recent error message, if any. */
  error: string | null;
  /** WebSocket connection state. */
  connected: boolean;
}

/**
 * Subscribe to an AppSync GraphQL subscription.
 *
 * Variables are intentionally NOT deep-compared — pass a stable
 * reference (memoize) if the variables depend on props/state.
 *
 * The callback is the preferred way to handle events — it's invoked
 * synchronously when data arrives, so the caller can do an
 * immediate state update without re-rendering twice.
 */
export function useAppSyncSubscription<T>(
  query: string,
  variables: Record<string, unknown> = {},
  onData?: (data: T) => void,
): SubscriptionState<T> {
  const [state, setState] = useState<SubscriptionState<T>>({
    data: null,
    error: null,
    connected: false,
  });
  // Store callback in a ref so we don't re-subscribe if it changes
  // identity (only if the query/variables change).
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  // Stable string of variables for the effect dep. Avoids re-subscribing
  // on every render due to new object identity.
  const varsKey = JSON.stringify(variables);

  useEffect(() => {
    if (!ENDPOINT) return;

    const host = graphqlHost(ENDPOINT);
    const auth = currentAuthHeader();
    const authHeaderJson = JSON.stringify({ host, Authorization: auth });
    const url = `${realtimeUrl(ENDPOINT)}?header=${b64(authHeaderJson)}&payload=${b64("{}")}`;

    let socket: WebSocket | null = new WebSocket(url, "graphql-ws");
    let subscriptionId = crypto.randomUUID();
    let cancelled = false;

    socket.onopen = () => {
      socket?.send(JSON.stringify({ type: "connection_init" }));
    };

    socket.onmessage = (ev) => {
      if (cancelled) return;
      let msg: { type?: string; payload?: unknown; id?: string } = {};
      try {
        msg = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      if (msg.type === "connection_ack") {
        // Send the start frame. AppSync wants the GraphQL payload
        // stringified, and the per-message auth header again.
        socket?.send(
          JSON.stringify({
            id: subscriptionId,
            type: "start",
            payload: {
              data: JSON.stringify({
                query,
                variables: JSON.parse(varsKey),
              }),
              extensions: {
                authorization: { host, Authorization: auth },
              },
            },
          }),
        );
      } else if (msg.type === "start_ack" && msg.id === subscriptionId) {
        setState((s) => ({ ...s, connected: true, error: null }));
      } else if (msg.type === "data" && msg.id === subscriptionId) {
        const payload = msg.payload as { data?: T; errors?: unknown[] };
        if (payload?.errors?.length) {
          setState((s) => ({ ...s, error: JSON.stringify(payload.errors) }));
          return;
        }
        const data = payload?.data ?? null;
        setState((s) => ({ ...s, data, error: null }));
        if (data && onDataRef.current) onDataRef.current(data);
      } else if (msg.type === "error") {
        setState((s) => ({ ...s, error: JSON.stringify(msg.payload) }));
      } else if (msg.type === "ka") {
        // Keep-alive ping; no-op.
      } else if (msg.type === "connection_error") {
        setState((s) => ({
          ...s,
          error: `connection_error: ${JSON.stringify(msg.payload)}`,
          connected: false,
        }));
      }
    };

    socket.onerror = () => {
      if (cancelled) return;
      setState((s) => ({ ...s, error: "WebSocket error", connected: false }));
    };

    socket.onclose = () => {
      setState((s) => ({ ...s, connected: false }));
    };

    return () => {
      cancelled = true;
      try {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ id: subscriptionId, type: "stop" }));
        }
        socket?.close();
      } catch {
        // ignore
      }
      socket = null;
    };
  }, [query, varsKey]);

  return state;
}
